import type {
  TaskTrackedSegment,
  TaskTrackedSummary,
  TaskTrackedSummaryInput,
} from "@components/modules/resource-planning/tasks/components/task-tracked-progress/types";
import { t } from "@lingui/macro";

export function taskTrackedSummary({
  tracked,
  budget,
  unassigned = 0,
}: TaskTrackedSummaryInput): TaskTrackedSummary {
  const unassignedTracked = Math.min(unassigned, tracked);
  const isOverBudget = budget > 0 && tracked > budget;
  const scale = Math.max(budget, tracked) || 1;

  // Over budget: tracked parts shrink to fit the budget, the overflow follows
  const fitToBudget = (seconds: number) =>
    isOverBudget ? (seconds * budget) / tracked : seconds;
  const segments: Omit<TaskTrackedSegment, "percent">[] = [
    {
      id: "assigned",
      seconds: tracked - unassignedTracked,
      color: "teal.500",
      label: t`Tracked by assignees`,
    },
    {
      id: "unassigned",
      seconds: unassignedTracked,
      color: "yellow.300",
      label: t`Tracked by unassigned users`,
    },
    isOverBudget
      ? {
          id: "over",
          seconds: tracked - budget,
          color: "red.300",
          label: t`Over budget`,
        }
      : {
          id: "remaining",
          seconds: budget - tracked,
          color: "transparent",
          label: t`Remaining budget`,
        },
  ];

  return {
    tracked,
    budget,
    isOverBudget,
    percentSpent: budget ? Math.round((tracked / budget) * 100) : null,
    segments: segments
      .filter((segment) => segment.seconds > 0)
      .map((segment) => ({
        ...segment,
        percent:
          ((segment.id === "over"
            ? segment.seconds
            : fitToBudget(segment.seconds)) /
            scale) *
          100,
      })),
  };
}
